import React from 'react'
import PropTypes from 'prop-types'
import { Skeleton } from 'antd'

import RoundsReportTableRow from './RoundsReportTableRow.jsx'
import ResidentRoundDetailsCard from './ResidentRoundDetailsCard.jsx'
import {
  getLatestRoundTime,
  isRoundOverdue,
  getDailyTotals,
} from './tableUtils'
import { Text } from '@lmc/ui-kit'

const renderTableHeader = () => {
  return (
    <Text style={styles.tableHeader} as="h5">
      <div style={styles.roundHeader}>Round</div>
      <div style={styles.statusHeader}>Status</div>
      <div style={styles.detailsHeader}>Details</div>
    </Text>
  )
}

const renderRoundDetails = (props) => {
  const { isOverdue, residents, time } = props

  return (
    <div style={styles.roundDetailsContainer}>
      {residents.map((residentRoundDetails) => {
        if (!residentRoundDetails.resident) return null

        return (
          <ResidentRoundDetailsCard
            key={`${time} ${residentRoundDetails.resident.id}`}
            isOverdue={isOverdue}
            {...residentRoundDetails}
          />
        )
      })}
    </div>
  )
}

const renderTableRows = (props) => {
  const { currentDate, latestRoundTime, rounds } = props

  return (
    <div>
      {rounds.map((round, index) => {
        const isFinalRow = index === rounds.length - 1
        const isOverdue = isRoundOverdue({
          round,
          latestRoundTime,
          currentDate,
        })

        return (
          <RoundsReportTableRow
            key={round.time}
            time={round.time}
            due={isOverdue ? 0 : round.scheduled?.due}
            notSigned={isOverdue ? round.scheduled?.due : 0}
            signed={round.scheduled?.signed}
            notAdministered={round.scheduled?.notAdministered}
            PRN={round.PRN}
            isFinalRow={isFinalRow}
          >
            {renderRoundDetails({
              residents: round.residents,
              time: round.time,
              isOverdue,
            })}
          </RoundsReportTableRow>
        )
      })}
    </div>
  )
}

const renderDailyTotalRow = (props) => {
  const { currentDate, latestRoundTime, rounds } = props

  const dailyTotals = getDailyTotals({
    currentDate,
    rounds,
    latestRoundTime,
  })

  return (
    <RoundsReportTableRow
      time="Daily Total"
      signed={dailyTotals.signed}
      due={dailyTotals.due}
      notSigned={dailyTotals.notSigned}
      notAdministered={dailyTotals.notAdministered}
      PRN={dailyTotals.PRN}
      isDailyTotal
    />
  )
}

/**
@description Renders the rounds report table.

@param {object} currentDate. A dayjs object of the current
date. Defaults to dayjs(), but can be set to any date.

@param {array} rounds. Array of round objects.

@returns {function} MedicationRoundsReportTable.
*/

const Spinner = () => (
  <div style={{ minHeight: '60vh', opacity: 0.6 }}>
    <Skeleton active size="large" />
  </div>
)

export default function MedicationRoundsReportTable(props) {
  const { currentDate, isLoading, rounds } = props

  if (isLoading || !rounds) return <Spinner />

  const latestRoundTime = getLatestRoundTime({ rounds, currentDate })

  return (
    <div>
      {renderTableHeader()}
      {renderTableRows({ ...props, latestRoundTime })}
      {renderDailyTotalRow({ ...props, latestRoundTime })}
    </div>
  )
}

MedicationRoundsReportTable.propTypes = {
  currentDate: PropTypes.object,
  rounds: PropTypes.array.isRequired,
}

const styles = {
  detailsHeader: {
    width: 420,
  },
  roundDetailsContainer: {
    marginBottom: 8,
  },
  roundHeader: {
    width: 90,
  },
  statusHeader: {
    width: 450,
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: '22px',
    marginBottom: 8,
    padding: '0px 16px',
  },
}
